/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-get, ember/no-observers, ember/no-runloop, warp-drive/no-legacy-request-patterns */
import { tracked } from '@glimmer/tracking';
import Controller from '@ember/controller';
import { service } from '@ember/service';
import EmberObject, { action, computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import { next } from '@ember/runloop';
import { isEmpty, isPresent } from '@ember/utils';
import moment from 'moment';
import EnrolmentStatus from 'linced-app/enums/enrolment-status';
import {
  chequeNoValidations,
  chequeBSBValidations,
  chequeAccountNoValidations,
  moneyOrderNumberValidations,
  moneyOrderTextValidations,
} from 'linced-app/utils/finance';
import PaymentMethod from '@linc-technologies/linced-common/enums/payment-method';
import PersonType from '@linc-technologies/linced-common/enums/person-type';
import ENV from '../../../config/environment';
import { observes } from '@ember-decorators/object';
import {
  byKeys,
  compact,
  mapBy,
  uniqBy,
  without,
} from '@linc-technologies/linced-common/utils/arrays';

const NO_ITEMS = Object.freeze([]);

export default class IndexController extends Controller {
  // Services
  @service
  features;

  @service
  fetch;

  @service
  finance;

  @service
  paperToaster;

  @service
  router;

  @service
  store;

  @tracked personId = '';

  PaymentMethod = PaymentMethod;

  PersonType = PersonType;

  chequeNoValidations = chequeNoValidations;

  chequeBSBValidations = chequeBSBValidations;

  chequeAccountNoValidations = chequeAccountNoValidations;

  moneyOrderNumberValidations = moneyOrderNumberValidations;

  moneyOrderTextValidations = moneyOrderTextValidations;

  @tracked
  itemsByLearner = EmberObject.create({});

  @tracked
  paymentItem = null;

  @tracked paymentAmount = null;

  @action
  setPaymentAmount(value) {
    this.paymentAmount = value;
  }

  @tracked paymentMethod = null;

  @tracked
  paymentComment = '';

  @action
  setPaymentComment(value) {
    this.paymentComment = value;
  }

  @tracked
  paymentPerson = null;

  @tracked
  paymentPersonName = '';

  @tracked
  paymentDate = moment().format('YYYY-MM-DD');

  @action
  setPaymentDate(value) {
    this.paymentDate = value;
  }

  @tracked
  paymentChequeNo = '';

  @action
  setPaymentChequeNo(value) {
    this.paymentChequeNo = value;
  }

  @tracked
  paymentChequeBSB = '';

  @action
  setPaymentChequeBSB(value) {
    this.paymentChequeBSB = value;
  }

  @tracked
  paymentChequeAccountNo = '';

  @action
  setPaymentChequeAccountNo(value) {
    this.paymentChequeAccountNo = value;
  }

  @tracked
  paymentMoneyOrderNumber = '';

  @action
  setPaymentMoneyOrderNumber(value) {
    this.paymentMoneyOrderNumber = value;
  }

  @tracked
  paymentMoneyOrderIssuer = '';

  @action
  setPaymentMoneyOrderIssuer(value) {
    this.paymentMoneyOrderIssuer = value;
  }

  @tracked
  paymentMoneyOrderPayee = '';

  @action
  setPaymentMoneyOrderPayee(value) {
    this.paymentMoneyOrderPayee = value;
  }

  @tracked
  apportionment = [];

  @computed('apportionment.@each.lineItem')
  get apportionedIds() {
    return this.apportionment.map((item) => item.lineItem.id);
  }

  get tillDisabled() {
    return this.finance.tillDisabled(this.paymentMethod);
  }

  @computed('paymentAmount', 'apportionment.@each.amount')
  get amountRemaining() {
    return parseFloat(
      this.apportionment.reduce((val, item) => {
        return parseFloat(val).toFixed(2) - parseFloat(item.amount).toFixed(2);
      }, this.paymentAmount),
    ).toFixed(2);
  }

  @computed('personId', 'model.people.@each.{id,fullName}')
  get sortedPeople() {
    let sorted = this.model.people
      .slice()
      .sort((a, b) => (a.fullName || '').localeCompare(b.fullName || ''));
    if (!isEmpty(this.personId)) {
      // For learner-specific quick payments, put the specified learner at the top of the list.
      let person = sorted.find((item) => item.id === this.personId);
      if (!isEmpty(person)) {
        sorted = without(sorted, person);
        sorted.unshift(person);
      }
    }
    return sorted;
  }

  @computed('model.people.@each.learner')
  get billPayers() {
    let billPayingContacts = [];
    this.model.people.forEach((person) => {
      billPayingContacts.push(...person.get('learner.billPayers'));
    });
    return uniqBy(billPayingContacts, 'caregiverId');
  }

  // availableAccounts contains a list of accounts which have had line items selected, but which do not yet
  // have a payment in advance apportioned to them.
  // For learner-specified quick payments, all siblings are available (if not already apportioned).
  // In all cases, former students are excluded.
  @computed(
    'personId',
    'model.accounts.[]',
    'apportionment.@each.{lineItem,account}',
  )
  get availableAccounts() {
    let availableAccounts;
    if (!isEmpty(this.personId)) {
      availableAccounts = this.model.accounts;
    } else {
      availableAccounts = uniqBy(
        compact(mapBy(this.apportionment, 'lineItem.account')),
        'id',
      );
    }
    let apportionedAccounts = compact(
      this.apportionment.map((item) => item.account),
    );

    let sortedAccounts = availableAccounts
      .filter((account) => {
        let enrolmentStatus = account.get(
          'person.learner.enrolmentStub.status',
        );
        return !(
          apportionedAccounts.includes(account) ||
          enrolmentStatus === EnrolmentStatus.FORMER
        );
      })
      .sort(byKeys('person.fullName'));

    if (!isEmpty(this.personId)) {
      // For learner-specific quick payments, put the specified learner at the top of the list.
      let account = sortedAccounts.find(
        (item) => item.person_id === this.personId,
      );
      if (!isEmpty(account)) {
        sortedAccounts = without(sortedAccounts, account);
        sortedAccounts.unshift(account);
      }
    }

    return sortedAccounts;
  }

  @alias('finance.availablePaymentMethods')
  paymentMethods;

  @tracked
  selectedAccount;

  @action
  setSelectedAccount(value) {
    this.selectedAccount = value;
  }

  @tracked
  submitInProgress;

  @observes('amount', 'name')
  _queryParamsChanged() {
    this.resetForm();
  }

  @action
  isLearnerFormer(person) {
    let status = person.get('learner.enrolmentStub.status');
    return status === EnrolmentStatus.FORMER;
  }

  // The learner ids that have a line item load. A load puts its resolved
  // array in itemsByLearner, because the template cannot read a Promise.
  #lineItemLoads = new Set();

  @action
  getLearnerLineItems(person) {
    let items = this.get(`itemsByLearner.${person.id}`);
    if (items) {
      return items;
    }
    if (!this.#lineItemLoads.has(person.id)) {
      this.#lineItemLoads.add(person.id);
      let cache = this.itemsByLearner;
      this.store
        .query('finance/line-item', { learnerId: person.id })
        .then((result) => {
          // Ignore a load that resetForm made old.
          if (cache === this.itemsByLearner) {
            this.set(`itemsByLearner.${person.id}`, result);
          }
        });
    }
    // Show no items until the load completes. This list is for display.
    return NO_ITEMS;
  }

  @action
  setPaymentMethod(method) {
    this.paymentMethod = method;
  }

  @action
  setPaidBy(billPayer) {
    this.paymentPerson = billPayer;
    this.paymentPersonName = billPayer.fullName;
  }

  @action
  setPaidByName(name) {
    this.paymentPerson = null;
    this.paymentPersonName = name;
  }

  @action
  apportion(item) {
    this.apportionment = [
      ...this.apportionment,
      {
        lineItem: item,
        amount: Math.min(this.amountRemaining, item.balance),
        comment: this.paymentComment,
      },
    ];
  }

  @action
  apportionToAccount() {
    this.apportionment = [
      ...this.apportionment,
      {
        account: this.selectedAccount,
        amount: this.amountRemaining,
        comment: this.paymentComment,
      },
    ];
    this.selectedAccount = null;
  }

  @action
  remove(portion) {
    this.apportionment = this.apportionment.filter((p) => p !== portion);
  }

  @action
  submitPayment() {
    this.submitInProgress = true;
    let accountId = this.get('apportionment.0.lineItem.account.id');
    if (isEmpty(accountId)) {
      // First item is a Payment in Advance
      accountId = this.get('apportionment.0.account.id');
    }
    let url = `${ENV.APP.apiHost}/finance/${ENV.APP.apiPath}/accounts/${accountId}/:pay`;
    let tillId;
    if (!this.tillDisabled && !isEmpty(this.get('finance.activeTill'))) {
      tillId = parseInt(this.get('finance.activeTill.id'), 10);
    }
    let amount = Math.round(this.paymentAmount * 100);
    if (this.paymentMethod !== PaymentMethod.CHEQUE) {
      // Clear cheque detail fields if the payment isn't by cheque.
      this.paymentChequeNo = '';
      this.paymentChequeBSB = '';
      this.paymentChequeAccountNo = '';
    }
    if (this.paymentMethod !== PaymentMethod.MONEY_ORDER) {
      // Clear MO detail fields if the payment isn't by Money Order.
      this.paymentMoneyOrderNumber = '';
      this.paymentMoneyOrderIssuer = '';
      this.paymentMoneyOrderPayee = '';
    }
    let data = {
      accountId: parseInt(accountId, 10),
      method: this.paymentMethod,
      comment: this.paymentComment,
      date: this.paymentDate,
      amount,
      apportionment: [],
      chequeNo: this.paymentChequeNo,
      chequeBSB: this.paymentChequeBSB,
      chequeAccountNo: this.paymentChequeAccountNo,
      moneyOrderNumber: this.paymentMoneyOrderNumber,
      moneyOrderIssuer: this.paymentMoneyOrderIssuer,
      moneyOrderPayee: this.paymentMoneyOrderPayee,
      tillId,
    };
    // Only personId OR personName can be set - not both.
    if (!isEmpty(this.paymentPerson)) {
      data.personId = this.get('paymentPerson.caregiverId');
    } else {
      data.personName = this.paymentPersonName;
    }
    this.apportionment.forEach((portion) => {
      if (isPresent(portion.lineItem)) {
        // Apportion to a Line Item
        data.apportionment.push({
          lineItemId: parseInt(portion.lineItem.get('id'), 10),
          amount: Math.round(portion.amount * 100),
          comment: portion.comment,
        });
      } else {
        // Apportion to an Account (Payment in Advance)
        // (only if it's not the account receiving the initial Payment)
        if (portion.account.get('id') !== accountId) {
          data.apportionment.push({
            accountId: parseInt(portion.account.get('id'), 10),
            amount: Math.round(portion.amount * 100),
            comment: portion.comment,
          });
        }
      }
    });
    let options = {
      data: JSON.stringify(data),
    };
    this.fetch
      .post(url, options)
      .then((response) => {
        this.paperToaster.show(`Payment added successfully.`, {
          duration: 4000,
          position: 'top right',
          toastClass: 'success',
        });
        this.resetForm();
        next(this, function () {
          let queryParams = {
            personId: this.personId,
          };
          this.router.transitionTo(
            'admin.quick-payment.receipt',
            response.transactionId,
            { queryParams },
          );
        });
      })
      .catch(() => {
        this.paperToaster.show('Error adding Payment!', {
          duration: 4000,
          position: 'top right',
          toastClass: 'warning',
        });
        if (!isEmpty(tillId)) {
          // Update till sessions, in case somebody else started one for this
          // till after the payment dialog was opened.
          this.finance.updateTillSessions();
        }
      })
      .then(() => {
        this.submitInProgress = false;
      });
  }

  resetForm() {
    this.paymentAmount = this.amount;
    this.paymentMethod = null;
    this.paymentComment = '';
    this.paymentPerson = null;
    this.paymentPersonName = '';
    this.paymentItem = null;
    this.paymentDate = moment().format('YYYY-MM-DD');
    this.paymentChequeNo = '';
    this.paymentChequeBSB = '';
    this.paymentChequeAccountNo = '';
    this.paymentMoneyOrderNumber = '';
    this.paymentMoneyOrderIssuer = '';
    this.paymentMoneyOrderPayee = '';
    this.apportionment = [];
    this.itemsByLearner = {};
    this.#lineItemLoads.clear();
  }
}
